import { useSetDocumentTitle } from 'hooks/useSetDocumentTitle';
import { CmsApiSearchLandingPage } from 'modules/cms/api/types/CmsApiSearchLandingPage';
import { SearchBody } from 'modules/search/components/Body/SearchBody';
import { SearchResultsHeader } from 'modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from 'modules/search/components/Results/Hits/SearchResultsHits';
import { SearchSidebar } from 'modules/search/components/Sidebar/SearchSidebar';
import { searchFacetsByType } from 'modules/search/constants/searchFacetsByType';
import { useSaveSearch } from 'modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from 'modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from 'modules/search/hooks/useSearchPageActions';
import { useMainSelector } from 'store/hooks/useMainSelector';

import { SearchDescriptionBody } from './SearchDescriptionBody';
import { useSearchLandingPageLifecycle } from './useSearchLandingPageLifecycle';

type Props = {
  content: CmsApiSearchLandingPage;
  isSsr: boolean;
};

export function SearchLandingPage({ content, isSsr }: Props) {
  const mainSearch = useMainSelector((state) => state.mainSearch);
  const results = mainSearch.resultsByPage[mainSearch.pageIndex.toString()];

  const searchType = content.filters.type;
  const searchFacets = searchFacetsByType[searchType];

  useSetDocumentTitle(
    content.location?.name
      ? `${content.name}, ${content.location.name}`
      : content.name,
  );

  const { saveSearch, saveableSearch } = useSaveSearch({ searchFacets });
  const { setSearchUrlPageIndex, setSearchUrlSort, clearSearchUrl } =
    useSearchPageActions({ isInSearchLandingPage: true });

  const { getSearchTrackingData } = useSearchAnalytics({});
  const { initialized } = useSearchLandingPageLifecycle({
    content,
    isSsr,
    getSearchTrackingData,
    searchFacets,
  });

  return (
    <SearchBody
      sidebar={
        <SearchSidebar
          initialized={initialized}
          isInSearchLandingPage
          searchFacets={searchFacets}
          allowAds={searchType === 'JOB'}
        />
      }
    >
      <SearchDescriptionBody content={content} />

      <SearchResultsHeader
        results={results}
        isSearching={mainSearch.isSearching}
        query={mainSearch.query}
        sort={mainSearch.sort}
        searchType={searchType}
        saveSearch={saveSearch}
        saveableSearch={saveableSearch}
        onChangeSort={setSearchUrlSort}
        searchFacets={searchFacets}
      />

      <SearchResultsHits
        results={results}
        isSearching={mainSearch.isSearching}
        searchType={searchType}
        searchLocation={mainSearch.searchLocation}
        saveSearch={saveSearch}
        saveableSearch={saveableSearch}
        clearAllFilters={clearSearchUrl}
        getSearchTrackingData={getSearchTrackingData}
        setPageIndex={setSearchUrlPageIndex}
        searchFacets={searchFacets}
      />
    </SearchBody>
  );
}
